// Maneuvers.js
// Represents a detected tack or gybe maneuver

/*
## Detection Criteria
Maneuvers are when True Wind Angle (twa field) sign changes (crossing through wind) and stays that way for until **Completion**.
- **Maneuver Types:**
  - **Tack:** When |twa| is < 70° crosses through the wind and |twa| remains < 70° till **Completion**
  - **Gybe:** When |twa| is > 110° crosses through the wind and |twa| remains > 110° till **Completion**
- **Crossing**: is when twa sign changes
- **Finish:** Maneuver is finished 5 seconds after crossing through wind
- **Completion:** Must continue on the new side for at least 30 seconds after **Finish**.

*/
import * as bd from './boat-data.js';

export const MANEUVER_TYPE = {
  TACK: "Tack",
  GYBE: "Gybe"
};

export const MANEUVER_FINISH_TACK = {
  PORT: "Port",
  STB: "Starboard"
};

// time frames for maneuvers in order, total time is sum of all 10 + 5 + 60 = 75s for maneuver + 60s prior for VMG calc = 2m15s
const PRIOR_VMG_WINDOW_SEC = 60;
const START_BEFORE_CROSSING_SEC = 10;
const FINISH_DELAY_SEC = 5;
const COMPLETION_DELAY_SEC = 60;

const TACK_ANGLE_THRESHOLD = 70;
const GYBE_ANGLE_THRESHOLD = 110;

export class Maneuvers {
  constructor(vesselData) {
    this.list = [];
    if (vesselData) {
      this.detectManeuvers(vesselData);
    }
  }

  getManeuvers() {
    return this.list;
  }

  detectManeuvers(frames) {
    this.list = [];
    if (!frames) return;
    
    for (let i = 1; i < frames.length; i++) {
      const prevTwaCentered = bd.convert360North0To180PosNeg(frames[i - 1].calc?.twa_avg_centered);
      const currTwaCentered = bd.convert360North0To180PosNeg(frames[i].calc?.twa_avg_centered);
      
      if (Maneuvers.checkCrossingCenteredTWA(prevTwaCentered, currTwaCentered)) {
        // console.log("Detect maneuver:"+frames[i].t.format('DD-MM HH:mm:ss')+" "+prevTwaCentered+" to "+currTwaCentered);
        const maneuver = this.attempCreateManeuver(frames, i);
        if (maneuver) {
          this.list.push(maneuver);
          i = frames.indexOf(maneuver.completionFrame);
        }
      }
    }
  }

  attempCreateManeuver(frames, crossingIdx) {
    const crossingFrame = frames[crossingIdx];
    const prevFrame = frames[crossingIdx - 1];
    
    const prevAvgTwaCentered = bd.convert360North0To180PosNeg(prevFrame.calc?.twa_avg_centered);
    const currAvgTwaCentered = bd.convert360North0To180PosNeg(crossingFrame.calc?.twa_avg_centered);
    
    // Find finish
    const finishTime = crossingFrame.t.clone().add(FINISH_DELAY_SEC, 'seconds');
    let finishIdx = crossingIdx;
    while (finishIdx < frames.length && frames[finishIdx].t.isBefore(finishTime)) {
      finishIdx++;
    }
    if (finishIdx >= frames.length) return null;
    
    // Find completion
    const completionTime = frames[finishIdx].t.clone().add(COMPLETION_DELAY_SEC, 'seconds');
    let completionIdx = finishIdx;
    while (completionIdx < frames.length && frames[completionIdx].t.isBefore(completionTime)) {
      completionIdx++;
    }
    if (completionIdx >= frames.length) return null;
    
    const completionTwaAngles = bd.averageAngle(frames.slice(crossingIdx, completionIdx + 1), f => f.calc?.twa_avg_centered);
    const avgCompletionTwaCentered = bd.convert360North0To180PosNeg(completionTwaAngles);

    if (Maneuvers.checkCrossingCenteredTWA(currAvgTwaCentered, avgCompletionTwaCentered)){
      console.log("went back to other side - skipping");
      return null;
    }

    const lastManeuverFinish = this.list.length > 0 ? this.list[this.list.length - 1].completionFrame.t : null;
    return Maneuver.create(prevAvgTwaCentered, currAvgTwaCentered, frames, crossingIdx, finishIdx, completionIdx, lastManeuverFinish);
  }

  static checkCrossingCenteredTWA(prevAngle, newAngle) {
    return prevAngle < 0 && newAngle >= 0 || prevAngle >= 0 && newAngle < 0;
  }

}

export class Maneuver {
  constructor(type, newTack, startFrame, crossingFrame, finishFrame, completionFrame) {
    this.type = type;
    this.newTack = newTack,
    this.startFrame = startFrame;
    this.crossingFrame = crossingFrame;
    this.finishFrame = finishFrame;
    this.completionFrame = completionFrame;
    this.priorVMG = null;
    this.startToCrossingVMG = null;
    this.startToCrossingTimeLost = null;
    this.crossingToCompletionVMG = null;
    this.crossingToCompletionTimeLost = null;
  }

  getCalloutAnglesDeg() {
    const s = this.perpendicularAngle(this.startFrame._cog);
    let m = this.perpendicularAngle(this.startFrame._cog, this.completionFrame._cog);
    let f = this.perpendicularAngle(this.completionFrame._cog);
    m = this.dropNarrowingAngle(s, m);
    f = this.dropNarrowingAngle(m,f);
    return [s,m,f];
  }
  
  dropNarrowingAngle(a1, a2) {
    const bearingDelta = bd.bearingDelta(a1, a2);
    if (bearingDelta < 0 && this.newTack == MANEUVER_FINISH_TACK.PORT
      || bearingDelta > 0 && this.newTack == MANEUVER_FINISH_TACK.STB) {
      return a1;
    }
    return a2;
  }

  perpendicularAngle(...angles) {
    const a = bd.averageAngle(angles);
    const rotation = this.newTack == MANEUVER_FINISH_TACK.STB ? 180 : 0;
    return ((a - 90 - rotation) % 360);
  }
  
  static create(prevAvgTwa, currAvgTwa, frames, crossingIdx, finishIdx, completionIdx, lastManeuverFinish) {
    const type = Maneuver.findManeuverType(prevAvgTwa, currAvgTwa);
    const newTack = Maneuver.newTackCenteredTWA(currAvgTwa);

    const startTime = frames[crossingIdx].t.clone().subtract(START_BEFORE_CROSSING_SEC, 'seconds');
    let startIdx = crossingIdx;
    while (startIdx > 0 && frames[startIdx].t.isAfter(startTime)) {
      startIdx--;
    }

    const maneuver = new Maneuver(type, newTack, frames[startIdx], frames[crossingIdx], frames[finishIdx], frames[completionIdx]);
    
    const priorStartTime = maneuver.startFrame.t.clone().subtract(PRIOR_VMG_WINDOW_SEC, 'seconds');
    let priorStartIdx = startIdx;
    while (priorStartIdx > 0 && frames[priorStartIdx].t.isAfter(priorStartTime)) {
      priorStartIdx--;
    }
    
    if (frames[priorStartIdx].t.isAfter(priorStartTime)) {
      console.log("insufficient data - race started too recently");
    } else if (lastManeuverFinish && frames[priorStartIdx].t.isSameOrBefore(lastManeuverFinish)) {
      console.log("insufficient data - previous maneuver overlaps prior window");
    } else {
      maneuver.priorVMG = bd.averageValue(frames.slice(priorStartIdx, startIdx), 2, f => f._vmg);
    }
    
    maneuver.startToCrossingVMG = bd.averageValue(frames.slice(startIdx, crossingIdx), 2, f => f._vmg);
    maneuver.startToCrossingTimeLost = (maneuver.priorVMG - maneuver.startToCrossingVMG)/maneuver.priorVMG * maneuver.crossingFrame.t.diff(maneuver.startFrame.t, 'seconds');
    maneuver.startToCrossingTimeLost = parseInt(maneuver.startToCrossingTimeLost.toFixed(0));
    maneuver.crossingToCompletionVMG = bd.averageValue(frames.slice(crossingIdx, completionIdx), 2, f => f._vmg);
    maneuver.crossingToCompletionTimeLost = (maneuver.priorVMG - maneuver.crossingToCompletionVMG)/maneuver.priorVMG * maneuver.completionFrame.t.diff(maneuver.crossingFrame.t, 'seconds');
    maneuver.crossingToCompletionTimeLost = parseInt(maneuver.crossingToCompletionTimeLost.toFixed(0));
    
    return maneuver;
  }

  static findManeuverType(prevAvgTwa, currAvgTwa) {
    let type = null;
    const absPrevTwa = Math.abs(prevAvgTwa);
    const absCurrTwa = Math.abs(currAvgTwa);
    // console.log(absPrevTwa + " " + absCurrTwa + " (" + prevAvgTwa + " " + currAvgTwa + ")");

    if (absPrevTwa < TACK_ANGLE_THRESHOLD && absCurrTwa < TACK_ANGLE_THRESHOLD) {
      type = MANEUVER_TYPE.TACK;
    } else if (absPrevTwa > GYBE_ANGLE_THRESHOLD && absCurrTwa > GYBE_ANGLE_THRESHOLD) {
      type = MANEUVER_TYPE.GYBE;
    } else {
      console.error("Neither tack nor Gybe, bad data?: Prev TWA "+prevAvgTwa+" new TWA "+currAvgTwa);
    }
    // console.log("Type:"+type);
    return type;
  }


  static newTackCenteredTWA(newAngle) {
    return newAngle >= 0 ? MANEUVER_FINISH_TACK.STB : MANEUVER_FINISH_TACK.PORT;
  }
}